import { apiClient } from "@/shared/api/client";
import type {
  BoardStudySource,
  NikolaGameReference,
  NikolaMessagePart,
  ReviewMomentReference,
  TimeControl,
} from "@chessla/shared";

export type { PendingAction, SendMessageResult } from "./chat-contracts";

export interface CreditBalanceResult {
  available: number;
  included: number;
  purchased: number;
  reserved: number;
}

export interface ConfirmPracticeSetResult {
  title: string;
  format: TimeControl;
  positionCount: number;
  createdAt: string;
  reused: boolean;
}

export interface CoachExportResult {
  filename: string;
  mediaType: "application/json" | "application/x-chess-pgn";
  content: string;
}

export const confirmPracticeSet = (draftId: string): Promise<ConfirmPracticeSetResult> =>
  apiClient<ConfirmPracticeSetResult>("/coach/practice-sets/confirm", {
    method: "POST",
    body: JSON.stringify({ draftId }),
  });

export const fetchCoachExport = (exportId: string): Promise<CoachExportResult> =>
  apiClient<CoachExportResult>(`/coach/exports/${encodeURIComponent(exportId)}`);

export const downloadCoachExport = async (exportId: string): Promise<void> => {
  const file = await fetchCoachExport(exportId);
  const url = URL.createObjectURL(new Blob([file.content], { type: file.mediaType }));
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = file.filename;
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const fetchChatCreditBalance = async (): Promise<CreditBalanceResult> => {
  const value = await apiClient<unknown>("/coach/credits/balance");
  if (
    !value ||
    typeof value !== "object" ||
    !["available", "included", "purchased", "reserved"].every((key) =>
      Number.isFinite((value as Record<string, unknown>)[key]),
    )
  ) {
    throw new Error("Credit balance unavailable");
  }
  return value as CreditBalanceResult;
};

export interface CreditEstimateResult {
  route: "chat" | "profile_insights";
  reserved: number;
  margin: string;
}

export const fetchChatCreditEstimate = (route: "chat" | "profile_insights" = "chat"): Promise<CreditEstimateResult> =>
  apiClient<CreditEstimateResult>(`/coach/credits/estimate?route=${route}`);

export interface CurrentSessionMessage {
  role: "user" | "assistant" | "system";
  content: string;
  createdAt: string;
  parts?: readonly NikolaMessagePart[];
}

export interface CurrentSessionResult {
  sessionId: string | null;
  messages: CurrentSessionMessage[];
}

export interface CreateCoachingSessionResult {
  sessionId: string;
  playerId: string;
  topic: string | null;
  createdAt: string;
}

export const createCoachingSession = (
  topic?: string,
  gameId?: string,
  study?: BoardStudySource,
): Promise<CreateCoachingSessionResult> =>
  apiClient<CreateCoachingSessionResult>("/coach/sessions", {
    method: "POST",
    body: JSON.stringify({ topic, gameId, study }),
  });

/**
 * Fetches the player's current resumable session (if any) with its
 * message history — lets the chat page continue "one long chat" across
 * a page reload instead of always starting a fresh session. A null
 * sessionId means there's nothing to resume (either the player has
 * never chatted, or their last session already finished/got
 * summarized) — the caller should create a new session in that case.
 */
export const fetchCurrentSession = (): Promise<CurrentSessionResult> =>
  apiClient<CurrentSessionResult>("/coach/sessions/current");

/**
 * Fetches a SPECIFIC session's message history, regardless of whether
 * it's the player's current active session — used when the player
 * clicks into an older session from the session list. fetchCurrentSession
 * only ever returns the single most recent unsummarized session, so it
 * silently returned nothing for any older session id.
 */
export const fetchSessionMessages = (sessionId: string): Promise<CurrentSessionResult> =>
  apiClient<CurrentSessionResult>(`/coach/sessions/${sessionId}/messages`);

export interface ReviewChatHandoffResult {
  reference: NikolaGameReference;
  cohort: TimeControl;
  eyebrow: string;
  summary: string;
  question: string;
  prompts: Array<{ label: string; prompt: string }>;
  draft: string;
}

export const fetchReviewChatHandoff = (reference: ReviewMomentReference): Promise<ReviewChatHandoffResult> =>
  apiClient<ReviewChatHandoffResult>(
    `/coach/games/${encodeURIComponent(reference.gameId)}/moments/${encodeURIComponent(reference.factId)}/chat-handoff`,
  );
